import { useFormik } from 'formik';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Select, TextFiled } from 'tsx/comps/form';
import { media, colors } from 'tsx/theme';
import { checkIcon } from './imgData';
import * as Yup from 'yup';
import { apiUrl } from 'tsx/util';

const Section = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 20px;

  .grid-item {
    padding: 50px 30px;
    border: 1px solid ${colors.primary};
    border-radius: 25px;
    display: flex;
    flex-direction: column;
    gap: 30px;
    grid-gap: 30px;

    &__graphic {
      height: 55px;
      background-color: ${colors.primary};
      padding: 0 25px;
      max-width: 420px;
      padding-bottom: 4px;

      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;

      p,
      h2 {
        color: white;
        white-space: nowrap;
      }
      h2 {
        font-size: 30px;
      }

      p {
        font-weight: 400;
      }

      &:after {
        content: '';
        position: absolute;
        right: -19px;
        top: 8px;
        width: 39px;
        height: 39px;
        background-color: ${colors.primary};
        transform: rotateZ(45deg);
        z-index: -1;
      }

      &--two {
        padding-left: 40px;
        max-width: 430px;

        &:before {
          content: '';
          position: absolute;
          left: -19px;
          top: 8px;
          width: 39px;
          height: 39px;
          background-color: white;
          transform: rotateZ(45deg);
        }
      }
    }
    .lightHead {
      background-color: ${colors.lp3};
      &:after {
        background-color: ${colors.lp3};
      }
    }
    .lightText {
      opacity: 0.5;
    }

    &__doc-title {
      margin-bottom: 10px;
    }
    form {
      .inputs {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 14px;
        margin: 0px 0 40px 0;
        .input-grid-inner {
          input,
          select {
            width: 100%;
          }
        }
      }
    }
  }
  ${media.lg(css`
    grid-template-columns: 1fr;
  `)};

  ${media.md(css`
    grid-template-columns: 1fr;
    .grid-item {
      padding: 30px 20px;

      &__graphic {
        height: 40px;
        p {
          font-size: 12px;
        }
        h2 {
          font-size: 18px;
        }
        margin-right: 20px;

        &:after {
          right: -14px;
          top: 6px;
          width: 28px;
          height: 28px;
        }

        &--two {
          padding-left: 40px;
          &:after {
            background-color: ${colors.lp3};
          }
          &:before {
            left: -14px;
            top: 6px;
            width: 28px;
            height: 28px;
          }
        }
      }
      form {
        select,
        input {
          font-size: 12px;
        }
      }
    }
  `)};
  ${media.sm(css`
    .grid-item {
      form {
        .inputs {
          grid-template-columns: 1fr;
        }
      }
    }
  `)};
`;
const appointmentTypeOptions = [
  { label: 'Phone', value: 0 },
  { label: 'In Person', value: 1 },
];

const mortgageTypeOptions = [
  { label: 'Discounted mortgages', value: 0 },
  { label: 'Later Life Lending', value: 1 },
  { label: 'Remortgages', value: 2 },
  { label: 'Buy-to-Let', value: 3 },
  { label: 'Credit Renew', value: 4 },
  { label: 'Discount Mortgage Scheme (DMS)', value: 5 },
  { label: 'Shared Ownership', value: 6 },
  { label: 'Help to Buy', value: 7 },
  { label: 'First Homes', value: 8 },
  { label: 'Self Build', value: 9 },
];

const appointmentTypeToValue: Record<string, number> = {
  Phone: 0,
  'In Person': 1,
};

const mortgageTypeToValue: Record<string, number> = {
  'Discounted mortgages': 0,
  'Later Life Lending': 1,
  Remortgages: 2,
  'Buy-to-Let': 3,
  'Credit Renew': 4,
  'Discount Mortgage Scheme (DMS)': 5,
  'Shared Ownership': 6,
  'Help to Buy': 7,
  'First Homes': 8,
  'Self Build': 9,
};

const validationSchema = Yup.object({
  appointmentType: Yup.string().required('Required'),
  mortgageType: Yup.string().required('Required'),
  firstName: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
  lastName: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
  emailAddress: Yup.string().email('Invalid email address').required('Required'),
  contactNumber: Yup.string().min(2, 'Min 2 characters').max(255, 'Max 255 characters').required('Required'),
});

const App = () => {
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const { values, handleChange, handleBlur, errors, touched, isValid, handleSubmit, isSubmitting } = useFormik({
    validationSchema,
    initialValues: {
      appointmentType: '',
      mortgageType: '',
      firstName: '',
      lastName: '',
      emailAddress: '',
      contactNumber: '',
    },
    onSubmit: async (values) => {
      setFormSubmitted(true);
      values.contactNumber = values.contactNumber.toString();
      const appointmentTypeValue = appointmentTypeToValue[values.appointmentType];
      const mortgageTypeValue = mortgageTypeToValue[values.mortgageType];
      const valuesToSend = { ...values, appointmentType: appointmentTypeValue, mortgageType: mortgageTypeValue };
      // console.log(valuesToSend);
      try {
        const response = await fetch(`/api/forms/book-appointment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(valuesToSend),
        });

        if (!response.ok) {
          console.error('Failed to submit form:', response.status, response.statusText);
          const responseData = await response.json();
          console.error('Response data:', responseData);
          return;
        }
      } catch (error) {
        console.error('An error occurred while submitting the form:', error);
      }
    },
  });

  return (
    <Section role="list">
      <div className="grid-item" role="listitem">
        <div className={'grid-item__graphic' + (isFormSubmitted ? ' lightHead' : ' ')}>
          <h2>Step 01</h2>
          <p>Book an appointment</p>
        </div>
        {!isFormSubmitted ? (
          <>
            <p>Need Advice? Unsure what’s right for you? Please book an appointment by completing the below form.</p>
            <form onSubmit={handleSubmit}>
              <div className="inputs">
                <div className="input-grid-inner">
                  <label htmlFor="appointmentTypeLabel" className="visually-hidden">
                    Select Appointment Type
                  </label>
                  <Select
                    id="appointmentType"
                    value={values.appointmentType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.appointmentType && !!errors.appointmentType}
                    aria-label="Select Appointment Type"
                  >
                    <option value="" disabled selected>
                      Appointment Type
                    </option>
                    {appointmentTypeOptions.map((option) => (
                      <option key={option.value} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {touched.appointmentType && errors.appointmentType && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.appointmentType}</span>
                    </div>
                  )}
                </div>
                <div className="input-grid-inner">
                  <label htmlFor="mortgageTypeLabel" className="visually-hidden">
                    Select Mortgage Type
                  </label>
                  <Select
                    id="mortgageType"
                    value={values.mortgageType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.mortgageType && !!errors.mortgageType}
                    $hasValue={!!values.mortgageType}
                  >
                    <option value="" disabled selected>
                      Mortgage Type
                    </option>
                    {mortgageTypeOptions.map((option) => (
                      <option key={option.value} value={option.label}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {touched.mortgageType && errors.mortgageType && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.mortgageType}</span>
                    </div>
                  )}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="First Name"
                    id="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.firstName && !!errors.firstName}
                  />
                  {touched.firstName && errors.firstName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.firstName}</span>
                    </div>
                  )}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Last Name"
                    id="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.lastName && !!errors.lastName}
                  />
                  {touched.lastName && errors.lastName && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.lastName}</span>
                    </div>
                  )}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Email"
                    id="emailAddress"
                    type="email"
                    value={values.emailAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.emailAddress && !!errors.emailAddress}
                  />
                  {touched.emailAddress && errors.emailAddress && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.emailAddress}</span>
                    </div>
                  )}
                </div>
                <div className="input-grid-inner">
                  <TextFiled
                    placeholder="Contact Number"
                    id="contactNumber"
                    value={values.contactNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.contactNumber && !!errors.contactNumber}
                    type="number"
                    onKeyPress={(event) => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                  {touched.contactNumber && errors.contactNumber && (
                    <div role="alert" aria-live="assertive" aria-relevant="all">
                      <span className="visually-hidden"> Error: {errors.contactNumber}</span>
                    </div>
                  )}
                </div>
              </div>

              <button className="btn" type="submit" disabled={!isValid || isSubmitting}>
                Book Appointment
              </button>
            </form>
          </>
        ) : (
          <div className="formSuccesContainer">
            <div className="checklistImg">
              <img src={checkIcon} alt="" />
            </div>
            <h5 style={{ marginTop: 10 }}>Thank you for booking your appointment with us! </h5>
            <p style={{ marginTop: 15 }}>Your request has been received.</p>
          </div>
        )}
      </div>
      <div className="grid-item" role="listitem">
        <div className="grid-item__graphic">
          <h2>Step 02</h2>
          <p>Prepare for your appointment</p>
        </div>
        <div>
          <p>
            One of our dedicated advisors will reach out to you to confirm your appointment and provide a comprehensive
            overview of everything you should bring with you.
          </p>
          <br />
          <p>
            In the meantime, we have many articles available on our knowledge hub that may be of interest, including
            guides to moving home, saving for your first deposit and much more
          </p>
          <br />
          <br />

          <a target="_blank" href="/knowledge-hub">
            <button className="btn">Visit the Knowledge Hub</button>
          </a>
        </div>
      </div>
    </Section>
  );
};

export default App;
